'use strict'

const PAGE_REFERENCE_KEYS = new Set([
  'parentUniqueTitle',
  'pageUniqueTitle',
  'childPageUniqueTitle',
  'linkedPageUniqueTitle'
])

function nextCopyUniqueTitle (uniqueTitle, taken) {

  const base = `${uniqueTitle}_copy`

  if (!taken.has(base)) return base

  let number = 2

  while (taken.has(`${uniqueTitle}_copy${number}`)) number += 1

  return `${uniqueTitle}_copy${number}`
}

function nextCopyTitle (title, taken) {

  const label = title == null || String(title).trim() === '' ? 'Page' : String(title)
  const base = `${label} Copy`

  if (!taken.has(base)) return base

  let number = 2

  while (taken.has(`${label} Copy ${number}`)) number += 1

  return `${label} Copy ${number}`
}

function descendantPages (rootUniqueTitle, pages) {

  const byParent = new Map()

  for (const page of pages || []) {

    if (!page || !page.parentUniqueTitle || !page.uniqueTitle) continue

    if (!byParent.has(page.parentUniqueTitle)) byParent.set(page.parentUniqueTitle, [])

    byParent.get(page.parentUniqueTitle).push(page)
  }

  const descendants = []
  const seen = new Set()
  const queue = (byParent.get(rootUniqueTitle) || []).slice()

  while (queue.length) {

    const page = queue.shift()

    if (!page || seen.has(page.uniqueTitle) || page.uniqueTitle === rootUniqueTitle) continue

    seen.add(page.uniqueTitle)
    descendants.push(page)

    for (const child of byParent.get(page.uniqueTitle) || []) queue.push(child)
  }

  return descendants
}

function planPageCopy (rootUniqueTitle, pages, options) {

  const includeSubPages = !options || options.includeSubPages !== false
  const list = Array.isArray(pages) ? pages : []
  const source = list.find((page) => page && page.uniqueTitle === rootUniqueTitle)

  if (!source) return null

  const sources = includeSubPages
    ? [source].concat(descendantPages(rootUniqueTitle, list))
    : [source]

  const takenUniqueTitles = new Set()
  const takenTitles = new Set()

  for (const page of list) {

    if (!page) continue

    if (page.uniqueTitle) takenUniqueTitles.add(page.uniqueTitle)

    if (page.title != null && page.title !== '') takenTitles.add(page.title)
  }

  const pageMap = {}
  const copies = []

  for (const page of sources) {

    const uniqueTitle = nextCopyUniqueTitle(page.uniqueTitle, takenUniqueTitles)
    const title = nextCopyTitle(page.title, takenTitles)

    takenUniqueTitles.add(uniqueTitle)
    takenTitles.add(title)
    pageMap[page.uniqueTitle] = uniqueTitle

    copies.push({
      sourceUniqueTitle: page.uniqueTitle,
      uniqueTitle,
      title,
      parentUniqueTitle: page.parentUniqueTitle && pageMap[page.parentUniqueTitle]
        ? pageMap[page.parentUniqueTitle]
        : (page.parentUniqueTitle || '')
    })
  }

  return { copies, pageMap }
}

function rewritePageReferences (node, pageMap) {

  if (Array.isArray(node)) {

    return node.map((item) => rewritePageReferences(item, pageMap))
  }

  if (!node || typeof node !== 'object') return node

  const rewritten = {}

  for (const [key, value] of Object.entries(node)) {

    if (key === '_id' || key === 'children') continue

    if (typeof value === 'string' && PAGE_REFERENCE_KEYS.has(key) && pageMap[value]) {

      rewritten[key] = pageMap[value]
      continue
    }

    if (value && typeof value === 'object') {

      rewritten[key] = rewritePageReferences(value, pageMap)
      continue
    }

    rewritten[key] = value
  }

  return rewritten
}

module.exports = {
  nextCopyUniqueTitle,
  nextCopyTitle,
  descendantPages,
  planPageCopy,
  rewritePageReferences
}
